import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function BuildingInsights({ unitsData, issueCounts }) {
  // 1) Identify "problem risers" logic (unchanged).
  const riserMap = {};
  unitsData.forEach((row) => {
    if (row.inspection === 'Needs Service') {
      const unitNum = row.unit || '';
      const lastTwo = unitNum.slice(-2);
      if (!riserMap[lastTwo]) {
        riserMap[lastTwo] = [];
      }
      riserMap[lastTwo].push(unitNum);
    }
  });

  const problemRisers = [];
  Object.keys(riserMap).forEach((lastTwo) => {
    if (riserMap[lastTwo].length > 11) {
      riserMap[lastTwo].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      problemRisers.push(riserMap[lastTwo]);
    }
  });

  // 2) Our label map for known issue columns
  // Note: Multiple field name variations map to the same display label (e.g., all drain pan variations)
  const labelMap = {
    thermostat:       'Thermostat',
    blower_fan:       'Blower Fan',
    actuator:         'Actuator',
    n3way_valves:     '3 Way Valves',
    drip_pan__sensor: 'Drain Pan',
    drip_pan_sensor:  'Drain Pan',  // Alternative field name
    drip_pan:         'Drain Pan',   // Alternative field name
    drain_pan:        'Drain Pan',   // Alternative field name
    drain_pan_sensor: 'Drain Pan',   // Alternative field name
    valves:           'Valves',
    hoses:            'Hoses',
    mould:            'Potential Mould',
    coils:            'Coils',
    compressor:       'Compressor',
    glycol_leakage:   'Glycol Leakage',
    leak_sensor:      'Leak Sensor',
    insulation:       'Insulation',
    system_failure:   'Other',  // just in case
  };
  
  // Helper function to normalize issue keys and combine counts for drain pan variations
  const normalizeIssueCounts = (counts) => {
    const normalized = {};
    const drainPanKeys = ['drip_pan__sensor', 'drip_pan_sensor', 'drip_pan', 'drain_pan', 'drain_pan_sensor'];
    let drainPanTotal = 0;
    
    Object.entries(counts).forEach(([key, count]) => {
      if (drainPanKeys.includes(key)) {
        // Combine all drain pan variations into one
        drainPanTotal += count;
      } else {
        normalized[key] = count;
      }
    });
    
    // Add combined drain pan count if any drain pan issues were found
    if (drainPanTotal > 0) {
      normalized['drip_pan__sensor'] = drainPanTotal; // Use the standard key
    }
    
    return normalized;
  };

  // 3) Normalize issue counts (combine drain pan variations) => filter out zero values => sort desc
  const normalizedCounts = normalizeIssueCounts(issueCounts);
  let filteredIssues = Object.entries(normalizedCounts)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]); // descending by count

  // 4) Create labels & data arrays
  const labels = filteredIssues.map(([key]) => labelMap[key] || key);
  const dataValues = filteredIssues.map(([, count]) => count);

  // 5) Build bar data
  const barData = {
    labels,
    datasets: [
      {
        label: 'Most Common Issues',
        data: dataValues,
        backgroundColor: '#2c59a2',
      },
    ],
  };

  // 6) Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: false },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
      },
    },
  };

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <h5 style={{ marginBottom: '1rem', fontWeight: '600' }}>Most Common Issues</h5>

      {filteredIssues.length === 0 ? (
        <div>No issues found</div>
      ) : (
        <div style={{ width: '100%', height: '325px' }}>
          <Bar data={barData} options={options} />
        </div>
      )}
    </div>
  );
}

export default BuildingInsights;
